import { RegisteredRouter } from "@tanstack/react-router"
import { AxiosError } from "axios"

type TRoutePath = RegisteredRouter["routeTree"]["types"]["fileRouteTypes"]["to"]

const REDIRECT_STATUS_CODES = [401, 403]

const SKIP_REDIRECT_ENDPOINTS = ["/auth/account/login"]

const ERROR_KEY_TO_REDIRECT_PATH: Record<string, TRoutePath> = {
  mfa_setup_required: "/mfa/setup",
  mfa_verification_required: "/mfa/validate",
}

/**
 * Redirects the user in response to certain auth-related API errors, keeping the
 * handling in one place. Registered as a response error interceptor on
 * `apiClient`; anything it doesn't act on rejects unchanged.
 */
export function authResponseErrorInterceptor(error: AxiosError) {
  const status = error.response?.status
  const path = error.config?.url

  if (
    status === undefined ||
    !REDIRECT_STATUS_CODES.includes(status) ||
    (path !== undefined && SKIP_REDIRECT_ENDPOINTS.includes(path))
  ) {
    return Promise.reject(error)
  }

  let redirectPath: TRoutePath | undefined
  if (status === 401) {
    // A 401 means the session is gone, so send them to log in.
    redirectPath = "/login"
  } else if (status === 403) {
    // A 403 names the required next step via `error_key`.
    const errorKey = (
      error.response?.data as { error_key?: string } | undefined
    )?.error_key
    redirectPath =
      errorKey !== undefined ? ERROR_KEY_TO_REDIRECT_PATH[errorKey] : undefined
  }

  if (redirectPath === undefined) {
    redirectPath = "/login"
  }

  // Outside React, so redirect with a full-page handoff.
  window.location.href = redirectPath

  return Promise.reject(error)
}
